<template>
  <div
    class="block-play-slotmachine"
    :class="{
      'is-on-grid': isOnGrid,
      'has-disabled-transition': !hasTransition,
      'is-skeleton': isSkeleton,
    }"
  >
    <div class="block-play-slotmachine__inner">
      <div class="block-play-slotmachine__intro">
        <AtomTextHeading
          :text="title"
          font-size="h3"
          class="block-play-slotmachine__intro-title"
        />
        <AtomTextHeading
          :text="text"
          font-size="h5"
          class="block-play-slotmachine__intro-text"
        />
      </div>

      <div class="block-play-slotmachine__machine">
        <div class="block-play-slotmachine__reels">
          <transition name="fade-in">
            <svg
              v-if="!isSkeleton"
              viewBox="0 0 58 49"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="block-play-slotmachine__stopper"
            >
              <path d="M3 24.6222L55.5 3.00007V46.2443L3 24.6222Z" fill="var(--c-luckyseven--triangle-border)" />
              <path fill-rule="evenodd" clip-rule="evenodd" d="M0.5 24.6221C0.499999 23.6091 1.11129 22.6963 2.04796 22.3105L54.548 0.688384C55.3192 0.37076 56.198 0.458659 56.891 0.92274C57.5841 1.38682 58 2.16595 58 3.00001V46.2442C58 47.0783 57.5841 47.8574 56.891 48.3215C56.198 48.7856 55.3192 48.8735 54.548 48.5558L2.04796 26.9338C1.11129 26.548 0.500001 25.6351 0.5 24.6221ZM9.56484 24.6221L53 42.5109V6.73336L9.56484 24.6221Z" fill="var(--c-luckyseven--triangle-bg)" />
            </svg>
          </transition>

          <div v-if="isSkeleton" class="block-play-slotmachine__reel" />
          <div v-if="isSkeleton" class="block-play-slotmachine__reel" />
          <div v-if="isSkeleton" class="block-play-slotmachine__reel" />

          <div
            v-for="(reel, index) of computedReels"
            :key="`reel-${index}`"
            class="block-play-slotmachine__reel"
            :class="[
              `is-state-${playState}`,
            ]"
          >
            <transition name="fade-in">
              <div
                v-if="!isSkeleton"
                class="block-play-slotmachine__reel-wrapper"
              >
                <AtomImage
                  v-if="setupBorder?.url"
                  :image="{
                    src: setupBorder.url,
                    alt: setupBorder.alt,
                  }"
                  behaviour="stretch"
                  class="block-play-slotmachine__reel-border"
                />
                <div
                  class="block-play-slotmachine__reel-spinner"
                  :style="`
                      --spinner-duration: calc(var(--slot-duration) * ${reel.length + 2});
                      --reel-length: ${reel.length + 2};
                  `"
                >
                  <div
                    ref="resultRef"
                    class="block-play-slotmachine__symbol is-start"
                  >
                    <AtomImage
                      v-if="findSymbol(randomStartSlots[index], reel)"
                      :image="findSymbol(randomStartSlots[index], reel)"
                    />
                  </div>

                  <div
                    v-for="(symbol, symbolIndex) of reel"
                    :key="`symbol-${symbolIndex}`"
                    class="block-play-slotmachine__symbol"
                  >
                    <AtomImage
                      v-if="symbol?.image?.url"
                      :disable-lazy="true"
                      :image="{
                        src: symbol.image.url,
                        alt: symbol.image.alt,
                      }"
                    />
                  </div>

                  <div
                    ref="resultRef"
                    class="block-play-slotmachine__symbol is-result"
                  >
                    <AtomImage
                      v-if="findSymbol(results[index], reel)"
                      :image="findSymbol(results[index], reel)"
                    />
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <transition name="fade-in">
            <svg
              v-if="!isSkeleton"
              viewBox="0 0 58 49"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="block-play-slotmachine__stopper"
            >
              <path d="M3 24.6222L55.5 3.00007V46.2443L3 24.6222Z" fill="var(--c-luckyseven--triangle-border)" />
              <path fill-rule="evenodd" clip-rule="evenodd" d="M0.5 24.6221C0.499999 23.6091 1.11129 22.6963 2.04796 22.3105L54.548 0.688384C55.3192 0.37076 56.198 0.458659 56.891 0.92274C57.5841 1.38682 58 2.16595 58 3.00001V46.2442C58 47.0783 57.5841 47.8574 56.891 48.3215C56.198 48.7856 55.3192 48.8735 54.548 48.5558L2.04796 26.9338C1.11129 26.548 0.500001 25.6351 0.5 24.6221ZM9.56484 24.6221L53 42.5109V6.73336L9.56484 24.6221Z" fill="var(--c-luckyseven--triangle-bg)" />
            </svg>
          </transition>
        </div>

        <AtomButton
          border-color="var(--c-secondary)"
          background-color="var(--c-secondary)"
          text-color="var(--c-primary)"
          alignment="center"
          :text="isWin
            ? winButtonText
            : spinButtonText"
          :is-disabled="isWin
            ? false
            : isDisabled || isRunning || isPending || isSkeleton"
          :callback="isWin
            ? navigateToPrizes
            : spin"
        />

        <div
          class="block-play-slotmachine__overlay"
          :class="{
            'is-visible': setupIsDisabled,
          }"
        >
          <AtomTextHeading
            :text="noSpinsLeftTitle"
            font-size="h3"
            class="block-play-slotmachine__overlay-title"
          />

          <AtomTextRichtext
            :html="compileWithHandlebars(noSpinsLeftText, data)"
            class="block-play-slotmachine__overlay-text"
          />
        </div>

        <transition name="fade-in">
          <AtomPopoverNotification
            v-if="isPopupVisible"
            :text="submitErrorText"
            :use-click-outside="true"
            :type="'error'"
            @on-hide="isPopupVisible = false"
          />
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
    isOnGrid: {
        type: Boolean,
        default: false,
    },

    isDisabled: {
        type: Boolean,
        default: false,
    },

    /* Content */
    title: {
        type: String,
        default: '',
    },

    text: {
        type: String,
        default: '',
    },

    spinButtonText: {
        type: String,
        default: '',
    },

    winButtonText: {
        type: String,
        default: '',
    },

    noSpinsLeftTitle: {
        type: String,
        default: '',
    },

    noSpinsLeftText: {
        type: String,
        default: '',
    },

    reels: {
        type: Array,
        default: () => [],
    },

    submitErrorText: {
        type: String,
        default: 'Fehler! Bitte versuche es erneut.',
    },

    /* Styles */
    introBackgroundColor: {
        type: String,
        default: 'var(--c-box--bg)',
    },

    machineBackgroundColor: {
        type: String,
        default: 'var(--c-black)',
    },

    border: {
        type: Object,
        default: () => ({}),
    },

    /* Wins button */
    winsButtonText: {
        type: String,
        default: 'Nochmals spielen',
    },

    winsButtonLink: {
        type: String,
        default: '/lucky-seven',
    },
});

const {
    introBackgroundColor,
    machineBackgroundColor,
} = toRefs(props);

const setupReels = ref(props.reels);
const setupBorder = ref(props.border);
const setupIsDisabled = ref(props.isDisabled);
const isSkeleton = ref(!isArrayNotEmpty(props.reels));

/*
    Helpers
*/
const amountOfDuplicatedArrays = 4;
const amountOfDuplicatedRandomElements = 4;
const shuffle = (array, duplicates = 0) => {
    // Pick x random elements from the array for the duplicates
    const randomElements = [];
    for (let i = 0; i < duplicates; i += 1) {
        const randomIndex = Math.floor(Math.random() * array.length);
        randomElements.push(array[randomIndex]);
    }

    // Create a new array with array and the random duplicates
    const duplicatedArrays = Array(
        amountOfDuplicatedArrays,
    ).fill(array).flat();

    const duplicatedRandomElements = Array(
        amountOfDuplicatedRandomElements,
    ).fill(randomElements).flat();

    // Create a new array with array and the random duplicates
    const shuffledArray = [
        ...duplicatedArrays,
        ...duplicatedRandomElements,
    ];

    for (let i = shuffledArray.length - 1; i > 0; i -= 1) {
        let j;
        do {
            j = Math.floor(Math.random() * (i + 1));
        } while (
            shuffledArray[i].symbol === shuffledArray[j].symbol
            && shuffledArray.slice(0, i).some((item) => item.symbol !== shuffledArray[i].symbol)
        );
        [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
    }

    return shuffledArray;
};

const findSymbol = (symbol, reel) => {
    const symboldObject = reel.find((reelSymbol) => reelSymbol.symbol === symbol);
    return symboldObject?.image?.url ? {
        src: symboldObject.image.url,
        alt: symboldObject.image.alt,
    } : null;
};

/*
    State
*/
const playState = ref('start');
const isRunning = ref(false);
const isWin = ref(false);
const hasTransition = ref(true);
const results = ref([
    '',
    '',
    '',
]);

const randomStartSlots = ref(props.reels?.map(
    (reel) => reel[Math.floor(Math.random() * reel.length)].symbol,
));

const computedReels = computed(
    () => (isArrayNotEmpty(setupReels.value)
        ? setupReels.value?.map((reel, index) => shuffle(reel, index))
        : []),
);

watch(() => playState.value, async (newValue) => {
    if (newValue === 'spinning') {
        const lastReel = computedReels.value[computedReels.value.length - 1];
        const lastReelLength = lastReel.length;
        const slotDuration = 150;

        await sleep(slotDuration * lastReelLength + 2 + 1000);

        playState.value = 'result';
    }

    if (newValue === 'result') {
        hasTransition.value = false;

        nextTick(() => {
            randomStartSlots.value = results.value;

            nextTick(() => {
                playState.value = 'start';

                nextTick(async () => {
                    hasTransition.value = true;
                    isRunning.value = false;
                    isWin.value = true;
                });
            });
        });
    }
});

/*
    Configuration
*/
const componentUid = getUid();
const spinsLeft = ref(0);

const {
    error,
    data,
} = await useLazyAsyncData(
    `lucky-seven/${componentUid}`,
    async () => Promise.all(
        [
            $fetch('/api/lucky-seven/me'),
            $fetch('/api/lucky-seven/config'),
        ],
    ),
    {
        transform: (response) => {
            if (!isArrayNotEmpty(response)) return {};

            const mergedData = Object.assign({}, ...response.map((res) => res));
            if (!mergedData.has_spins_left) {
                setupIsDisabled.value = true;
            }

            if (!isArrayNotEmpty(mergedData.reels)) return {};

            randomStartSlots.value = mergedData.reels.map(
                (reel) => reel[Math.floor(Math.random() * reel.length)].symbol,
            );

            isSkeleton.value = false;
            setupReels.value = mergedData.reels;
            spinsLeft.value = mergedData.spins_left;
            setupBorder.value = mergedData.border;

            return mergedData;
        },
        server: false,
    },
);

watch(() => error.value, (newValue) => {
    if (newValue) {
        useSentryError(newValue, {
            useThrow: false,
        });
    }
});

/*
    Play
*/
const isPending = ref(false);
const isPopupVisible = ref(false);
const spin = async () => {
    if (props.isDisabled || isRunning.value || isPending.value) return;
    try {
        isPending.value = true;

        const result = await $fetch('/api/lucky-seven/play');

        isRunning.value = true;
        playState.value = 'spinning';
        results.value = result.result;

        setCurrentWins(
            [
                { prize: result.prize },
            ],
            {
                link: props.winsButtonLink,
                text: props.winsButtonText,
                isVisible: spinsLeft.value <= 1,
            },
        );
    } catch (e) {
        isPopupVisible.value = true;

        useSentryError(e, {
            useThrow: false,
            response: e,
        });
    } finally {
        isPending.value = false;
    }
};

const navigateToPrizes = async () => {
    await navigateTo('/current-wins');
};
</script>

<style lang="scss" scoped>
.block-play-slotmachine {
    &.is-on-grid {
        @include wrapper-layout;
        @include grid-layout;
        margin-bottom: var(--m-bottom--big);
    }
}

.block-play-slotmachine__inner {
    .is-on-grid & {
        @include default-content-columns('wide');
    }
}

.block-play-slotmachine__intro {
    @include box-default;
    display: flex;
    flex-direction: column;
    background-color: v-bind(introBackgroundColor);
    row-gap: 20px;
}

.block-play-slotmachine__machine {
    @include fluid-simple('padding-left', 30px, 157px);
    @include fluid-simple('padding-right', 30px, 157px);
    @include fluid-simple('padding-top', 20px, 86px);
    @include fluid-simple('padding-bottom', 20px, 86px);
    @include fluid-simple('row-gap', 20px, 50px);
    position: relative;

    display: flex;
    flex-direction: column;
    background-color: v-bind(machineBackgroundColor);
}

.block-play-slotmachine__intro-text,
.block-play-slotmachine__intro-title {
    max-width: 613px;
}

.block-play-slotmachine__overlay {
    @include z-index('slotmachineOverlay');
    @include box-padding();

    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(5px);
    background-color: var(--c-secondary--transparent);
    opacity: 0;
    pointer-events: none;
    row-gap: 20px;
    transition: opacity 0.25s ease-in-out;

    &.is-visible {
        opacity: 1;
        pointer-events: all;
    }
}

.block-play-slotmachine__overlay-text,
.block-play-slotmachine__overlay-title {
    width: 100%;
    max-width: 723px;
}

/*
    Reels
*/
.block-play-slotmachine__reels {
    position: relative;
    display: flex;
    flex-direction: row;
    column-gap: 20px;
    --slot-duration: 0.15s;
    --spinner-duration: 4s;

    .is-skeleton & {
        @include skeleton-loading();
    }
}

.block-play-slotmachine__stopper {
    @include z-index('slotmachineStopper');
    @include fluid-simple('--stopper-width', 17px, 58px);
    @include fluid-simple('--stopper-height', 14px, 49px);

    position: absolute;
    top: 0;
    bottom: 0;
    overflow: visible;
    width: var(--stopper-width, 58px);
    height: var(--stopper-height, 49px);
    margin: auto;
    transform: rotate(180deg) translateX(16%);

    &:last-child {
        right: 0;
        transform: rotate(0deg) translateX(16%);
    }
}

.block-play-slotmachine__reel-wrapper, {
    position: relative;
    width: 100%;
    height: 100%;
}

@keyframes spinner {
    0% { transform: translateY(0); }
    100% { transform: translateY(-100%); }
}

.block-play-slotmachine__reel-spinner {
    display: flex;
    flex-direction: column;
    transition: transform var(--spinner-duration) var(--ease-out--slotmachine);

    .is-state-spinning &,
    .is-state-result & {
        transform: translateY(calc(0% - (100% - (100% / var(--reel-length)))));
    }

    .has-disabled-transition &,
    .is-state-result &,
    .is-state-start & {
        transition-duration: 0s;
    }
}

.block-play-slotmachine__reel {
    overflow: hidden;
    flex-grow: 1;
    aspect-ratio: 0.75;

}

.block-play-slotmachine__symbol {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    aspect-ratio: 0.75;
}

/*
    Images
*/
.block-play-slotmachine__reel-border {
    @include z-index('slotmachineBorder');
}
</style>
